import React from "react";
import { Link } from "react-router-dom";
import useSEO from "../hooks/useSEO";
import Photo from "../components/Photo";
import Reveal from "../components/Reveal";
import { Arrow } from "../components/Icons";
import { PageHead, SectionHeading, Icon, CTABand, FAQ, faqJsonLd } from "../components/Shared";
import { services, process, techStack } from "../data/services";
import { faqs } from "../data/content";

export default function Services() {
  useSEO({
    title: "Software Development Services — AI, Web, Mobile & Cloud",
    description: "Explore Victrie's software development services: AI & machine learning, custom software, web and mobile app development, cloud & DevOps, UI/UX design, data engineering and cybersecurity. Nairobi, Kenya.",
    path: "/services",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: services.map((s, i) => ({
        "@type": "Service",
        position: i + 1,
        name: s.title,
        description: s.short,
        provider: { "@type": "Organization", name: "Victrie" },
        areaServed: "Worldwide",
      })),
    },
  });

  return (
    <>
      <PageHead
        crumb="Services"
        title={<>Solutions that put you <span className="grad-text">years ahead</span></>}
        text="Whether you need an AI strategy, a world-class mobile app or a full digital transformation, Victrie brings senior engineers, designers and cloud specialists together under one roof."
      />

      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container" style={{ display: "grid", gap: 40 }}>
          {services.map((s, i) => (
            <Reveal key={s.slug} id={s.slug} className={`card split ${i % 2 ? "reverse" : ""}`} style={{ padding: "clamp(24px,4vw,48px)", scrollMarginTop: 100 }}>
              <Photo image={s.image} alt={`Visual for ${s.title}`} hover style={{ aspectRatio: "4/3" }} />
              <div>
                <div className="icon-badge"><Icon name={s.icon} /></div>
                <h2 style={{ fontSize: "clamp(1.4rem, 2vw, 1.8rem)" }}>{s.title}</h2>
                <p className="lead" style={{ fontSize: "1.05rem" }}>{s.long}</p>
                <ul style={{ listStyle: "none", padding: 0, display: "flex", flexWrap: "wrap", gap: 8, margin: "20px 0 28px" }}>
                  {s.tags.map((t) => <li key={t} className="tag">{t}</li>)}
                </ul>
                <Link to="/contact" className="btn btn--primary">Discuss this service <Arrow /></Link>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container">
          <SectionHeading eyebrow="Technology" title="A modern stack, chosen for your goals" text="We are technology-agnostic and outcome-obsessed. These are the tools we reach for most often." center />
          <div className="tech-grid">
            {techStack.map((t, i) => <Reveal key={t} delay={(i % 6) * 40} as="div">{t}</Reveal>)}
          </div>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container">
          <SectionHeading eyebrow="Process" title="From first call to launch day" center />
          <div className="grid grid--3">
            {process.map((p, i) => (
              <Reveal key={p.title} delay={i * 70} className="card">
                <span className="grad-text" style={{ fontFamily: "var(--font-head)", fontWeight: 700, fontSize: "2rem" }}>0{i + 1}</span>
                <h3 style={{ marginTop: 8 }}>{p.title}</h3>
                <p style={{ margin: 0 }}>{p.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container">
          <SectionHeading eyebrow="FAQ" title="Frequently asked questions" center />
          <FAQ items={faqs} />
        </div>
      </section>

      <CTABand title="Not sure which service you need?" text="Book a free 30-minute consultation. We will map your goals to the right solution and give you an honest estimate." />
    </>
  );
}

export const servicesFaqLd = faqJsonLd(faqs);
